import { Component, inject } from '@angular/core';
import {
    FormBuilder,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { finalize } from 'rxjs';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';

import { AuthService } from '../../../core/auth/services/auth.service';
import { ApiError } from '../../../core/auth/models/api-error';


@Component({
    selector: 'app-login',

    imports: [
        ReactiveFormsModule,
        NzButtonModule,
        NzCheckboxModule,
        NzIconModule,
        NzInputModule
    ],

    templateUrl: './login.html',
    styleUrl: './login.css'
})
export class Login {

    private readonly fb =
        inject(FormBuilder);

    private readonly router =
        inject(Router);

    private readonly authService =
        inject(AuthService);


    passwordVisible = false;

    loading = false;

    errorMessage = '';


    loginForm = this.fb.nonNullable.group({

        email: [
            '',
            [
                Validators.required,
                Validators.email
            ]
        ],

        password: [
            '',
            [
                Validators.required,
                Validators.minLength(8)
            ]
        ],

        rememberMe: [
            true
        ]

    });


    togglePassword(): void {

        this.passwordVisible =
            !this.passwordVisible;
    }


    login(): void {

        /*
         * Evitamos doble submit mientras
         * el login está procesándose.
         */
        if (this.loading) {
            return;
        }


        /*
         * Validamos formulario.
         */
        if (this.loginForm.invalid) {

            this.loginForm.markAllAsTouched();

            return;
        }


        /*
         * Limpiamos errores anteriores.
         */
        this.errorMessage = '';

        this.loading = true;


        /*
         * Obtenemos datos del formulario.
         */
        const {
            email,
            password,
            rememberMe
        } = this.loginForm.getRawValue();


        /*
         * LOGIN REAL
         *
         * POST:
         * http://localhost:8080/api/v1/auth/login
         *
         * Después AuthService hará:
         *
         * GET:
         * http://localhost:8080/api/v1/users/me
         */
        this.authService
            .login(
                {
                    email: email.trim(),
                    password
                },
                rememberMe
            )
            .pipe(

                /*
                 * Siempre quitamos el loading,
                 * tanto si funciona como si falla.
                 */
                finalize(() => {

                    this.loading = false;

                })

            )
            .subscribe({

                /*
                 * LOGIN EXITOSO
                 */
                next: () => {

                    void this.router.navigate([
                        '/dashboard'
                    ]);

                },


                /*
                 * LOGIN FALLIDO
                 */
                error: (
                    error: HttpErrorResponse
                ) => {

                    this.handleLoginError(
                        error
                    );

                }

            });
    }


    private handleLoginError(
        error: HttpErrorResponse
    ): void {

        /*
         * status 0 normalmente significa:
         *
         * - backend apagado
         * - CORS
         * - problema de red
         */
        if (error.status === 0) {

            this.errorMessage =
                'No se pudo conectar con RestaurantOS. Verifica que el servicio de identidad esté funcionando.';

            return;
        }


        const apiError =
            error.error as ApiError | null;


        /*
         * Errores que actualmente devuelve
         * tu Identity Service.
         */
        switch (apiError?.code) {

            case 'INVALID_CREDENTIALS':

                this.errorMessage =
                    'Correo o contraseña incorrectos.';

                return;


            case 'USER_INACTIVE':

                this.errorMessage =
                    'Tu cuenta se encuentra inactiva.';

                return;


            case 'FORBIDDEN':

                this.errorMessage =
                    'No tienes autorización para realizar esta operación.';

                return;

        }


        /*
         * Fallback por HTTP status.
         */
        if (error.status === 401) {

            this.errorMessage =
                'Correo o contraseña incorrectos.';

            return;
        }


        if (error.status === 403) {

            this.errorMessage =
                'Tu cuenta no tiene acceso al sistema.';

            return;
        }


        if (error.status === 400) {

            this.errorMessage =
                'Los datos ingresados no son válidos.';

            return;
        }


        if (error.status >= 500) {

            this.errorMessage =
                'RestaurantOS está teniendo un problema temporal. Intenta nuevamente.';

            return;
        }


        /*
         * Error genérico.
         */
        this.errorMessage =
            'No se pudo iniciar sesión. Intenta nuevamente.';
    }
}